import React from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Search,
  Shield,
  Target,
  Wrench,
  CheckCircle2,
  Award,
  BarChart3,
  RefreshCcw,
  Lightbulb
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';

export const EightDDetailContent = () => {
  const importancePoints = [
    {
      title: 'Structured Problem-Solving',
      icon: Target,
      description: '8D provides a clear, step-by-step approach, ensuring problems are addressed systematically rather than reactively.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Effective Root Cause Analysis',
      icon: Search,
      description: 'Training focuses on identifying true root causes instead of treating symptoms, leading to permanent solutions.',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Permanent Corrective Actions',
      icon: Wrench,
      description: '8D ensures solutions are sustainable and prevent the recurrence of problems, reducing repeat defects and failures.',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: 'Customer-Focused Resolution',
      icon: Shield,
      description: 'The methodology prioritizes customer protection and satisfaction, making it ideal for handling complaints and non-conformities.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Compliance with Quality Standards',
      icon: Award,
      description: '8D problem-solving is commonly aligned with standards such as ISO 9001, IATF 16949, ISO 13485, and AS9100.',
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  const disciplines = [
    {
      step: 'D1',
      title: 'Establish the Team',
      description: 'The first step focuses on forming a cross-functional team with the necessary knowledge, skills, and authority to solve the problem.',
      topics: ['Team roles and responsibilities', 'Effective collaboration', 'Communication and leadership']
    },
    {
      step: 'D2',
      title: 'Define the Problem',
      description: 'In this discipline, the problem is clearly defined using data and facts. A well-defined problem sets the foundation for effective analysis.',
      topics: ['Problem statements', '5W2H (What, Why, Where, When, Who, How, How many)', 'Impact assessment']
    },
    {
      step: 'D3',
      title: 'Implement and Verify Interim Containment Actions',
      description: 'Interim containment actions protect the customer while the root cause is being investigated. This step ensures no further impact while permanent solutions are developed.',
      topics: ['Immediate risk containment', 'Verification of containment effectiveness', 'Customer communication']
    },
    {
      step: 'D4',
      title: 'Identify Root Causes',
      description: 'Root cause analysis is the heart of the 8D methodology. Participants learn how to distinguish between symptoms and true root causes.',
      topics: ['5 Why analysis', 'Fishbone (Cause-and-Effect) diagrams', 'Data analysis and validation']
    },
    {
      step: 'D5',
      title: 'Develop Permanent Corrective Actions',
      description: 'In this step, teams identify corrective actions that eliminate the root cause. Corrective actions must address the root cause, not just the effect.',
      topics: ['Solution brainstorming', 'Risk assessment', 'Feasibility and effectiveness evaluation']
    },
    {
      step: 'D6',
      title: 'Implement and Validate Corrective Actions',
      description: 'Once selected, corrective actions are implemented and validated. Validation ensures the problem has been resolved permanently.',
      topics: ['Action planning and execution', 'Effectiveness monitoring', 'Verification using data']
    },
    {
      step: 'D7',
      title: 'Prevent Recurrence',
      description: 'This discipline focuses on system-level improvements to avoid similar problems in the future. Preventive actions strengthen the organization\'s quality system.',
      topics: ['Updating procedures and standards', 'Training and awareness', 'Lessons learned documentation']
    },
    {
      step: 'D8',
      title: 'Recognize the Team',
      description: 'The final step emphasizes recognizing the team\'s efforts and success. Recognition motivates employees and reinforces problem-solving discipline.',
      topics: ['Team appreciation', 'Knowledge sharing', 'Building a culture of continuous improvement']
    }
  ];

  const trainingBenefits = [
    'Improved root cause identification',
    'Reduced repeat problems and defects',
    'Faster and more effective problem resolution',
    'Enhanced customer satisfaction',
    'Stronger corrective and preventive action systems',
    'Better teamwork and communication',
    'Improved compliance with quality standards'
  ];

  const targetAudience = [
    {
      title: 'Quality Managers and Engineers',
      description: 'Professionals responsible for maintaining and improving product and process quality through structured problem-solving methods.'
    },
    {
      title: 'Manufacturing and Process Engineers',
      description: 'Engineers who need to resolve production issues, eliminate defects, and optimize manufacturing processes.'
    },
    {
      title: 'Internal and Lead Auditors',
      description: 'Auditors who assess corrective action effectiveness and ensure compliance with quality management standards.'
    },
    {
      title: 'Supplier Quality Professionals',
      description: 'Professionals managing supplier performance, handling non-conformities, and driving corrective actions across the supply chain.'
    },
    {
      title: 'Operations and Production Teams',
      description: 'Teams involved in day-to-day operations who need practical problem-solving skills to reduce downtime and defects.'
    },
    {
      title: 'Continuous Improvement Practitioners',
      description: 'Six Sigma, Lean, and Kaizen practitioners who apply structured methodologies to drive organizational improvement.'
    }
  ];

  const applicationIndustries = [
    'Manufacturing and automotive industries',
    'Aerospace and defense',
    'Medical devices and healthcare',
    'Electronics and engineering',
    'Supplier quality management'
  ];

  return (
    <div className="space-y-16">
      {/* What Is 8D Problem-Solving Training - H2 */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What Is 8D Problem-Solving Training?
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                8D Problem-Solving Training is a structured learning program that teaches participants how to apply the Eight Disciplines methodology to resolve complex problems. Originally developed by Ford Motor Company, the 8D methodology is now widely used across industries as a standard problem-solving and corrective action framework.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                8D training helps professionals:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {[
                  'Identify and define problems clearly',
                  'Analyze root causes using data',
                  'Implement effective corrective actions',
                  'Prevent recurrence of issues',
                  'Improve customer satisfaction and quality performance'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                The methodology emphasizes teamwork, discipline, and data-driven decision-making.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Importance Section - H2 */}
      <section className="py-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Importance of 8D Training in Quality Management
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {importancePoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <motion.div
                  key={point.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      <div className={`${point.bgColor} w-14 h-14 rounded-xl flex items-center justify-center mb-4`}>
                        <Icon className={`h-7 w-7 ${point.color}`} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{point.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{point.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 8D Methodology Overview - H2 with H3/H4 sub-sections */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Overview of the 8D Problem-Solving Methodology
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                A core component of 8D training is understanding and applying each of the eight disciplines effectively.
              </p>
              <div className="space-y-6">
                {disciplines.map((discipline, index) => (
                  <motion.div
                    key={discipline.step}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                    className="p-6 bg-gray-50 rounded-lg border-l-4 border-blue-600"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                        {discipline.step}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900">{discipline.title}</h3>
                    </div>
                    <p className="text-gray-700 mb-3">{discipline.description}</p>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Training Focus:</h4>
                      <ul className="space-y-2">
                        {discipline.topics.map((topic, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{topic}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits - H3 */}
      <section className="py-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                Benefits of 8D Problem-Solving Training
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {trainingBenefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                    className="flex items-start gap-3 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg"
                  >
                    <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Attend - H3 */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Who Should Attend 8D Training?
              </h3>
              <p className="text-lg text-gray-700 mb-8">
                8D problem-solving training is ideal for both beginners and experienced professionals:
              </p>
              <div className="space-y-4">
                {targetAudience.map((audience, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                    className="p-5 bg-white rounded-lg shadow-sm border-l-4 border-blue-600"
                  >
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{audience.title}</h4>
                    <p className="text-gray-600">{audience.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Applications - H3 */}
      <section className="py-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Applications of 8D Problem-Solving
              </h3>
              <p className="text-lg text-gray-700 mb-6">
                8D methodology is widely used across multiple industries and sectors:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {applicationIndustries.map((industry, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg border border-blue-200">
                    <CheckCircle2 className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{industry}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
